import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import cleanDeep from 'clean-deep';
import { useContext } from 'react';
import Maybe from 'true-myth/maybe';

import { ApplicationContext } from '../../../../../shared/source/context/context';
import {
    workHistoryItemArraySchema,
    workHistoryItemSchema,
} from '../../../../../shared/source/data-contract/work-history';
import { getUserHardSoftSkillsQueryKey } from '../../../../../shared/source/query-hooks/hard-soft-skills';
import { nullifyEmptyStringValues } from '../../common/form';

import type {
    WorkHistoryCreation,
    WorkHistoryItem,
    WorkHistoryMutation,
} from '../../../../../shared/source/data-contract/work-history';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

export const useWorkHistoryQuery = (userId: string, enabled: boolean): UseQueryResult<readonly WorkHistoryItem[]> => {
    const {
        applicationState: { language },
        validatingCobrainerApiFetcher,
    } = useContext(ApplicationContext);
    const workHistoryUrl = `/users/${userId}/work-history` as const;

    return useQuery({
        enabled,
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(workHistoryItemArraySchema, {
                pathname: workHistoryUrl,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [workHistoryUrl, 'GET', language.code],
    });
};

export const useOneWorkHistoryQuery = (
    userId: string,
    workHistoryId: string,
    enabled: boolean,
): UseQueryResult<WorkHistoryItem> => {
    const {
        applicationState: { language },
        validatingCobrainerApiFetcher,
    } = useContext(ApplicationContext);
    const oneWorkHistoryUrl = `/users/${userId}/work-history/${workHistoryId}` as const;

    return useQuery({
        enabled,
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(workHistoryItemSchema, {
                pathname: oneWorkHistoryUrl,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [oneWorkHistoryUrl, 'GET', language.code],
    });
};

export type WorkHistoryMutations = {
    readonly createWorkMutation: UseMutationResult<WorkHistoryItem, unknown, Omit<WorkHistoryItem, 'id'>>;
    readonly deleteWorkMutation: UseMutationResult<void, unknown, string>;
    readonly patchWorkMutation: UseMutationResult<WorkHistoryItem, unknown, WorkHistoryItem>;
};

export const useWorkHistoryMutations = (userId: string): WorkHistoryMutations => {
    const queryClient = useQueryClient();
    const { cobrainerApiFetcher, validatingCobrainerApiFetcher } = useContext(ApplicationContext);

    const invalidateWorkCache = async (workHistoryId: Maybe<string>): Promise<void> => {
        if (workHistoryId.isJust) {
            await queryClient.invalidateQueries({
                queryKey: [`/users/${userId}/work-history/${workHistoryId}`, 'GET'],
            });
        }
        await queryClient.invalidateQueries({ queryKey: [`/users/${userId}/work-history`, 'GET'] });
        await queryClient.invalidateQueries({ queryKey: getUserHardSoftSkillsQueryKey(userId) });
    };

    const createWorkMutation = useMutation({
        mutationFn: async (values: Omit<WorkHistoryItem, 'id'>): Promise<WorkHistoryItem> => {
            const { companyName, description, jobTitle, skills, ...restValues } = values;
            const data: WorkHistoryCreation = {
                ...cleanDeep(restValues),
                companyName,
                jobTitle,
                skillIds: skills?.map((skill) => skill.id) ?? [],
            };

            const result = await validatingCobrainerApiFetcher.postJson(workHistoryItemSchema, {
                pathname: `/users/${userId}/work-history`,
                payload: data,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        onSuccess: (values: WorkHistoryItem) => invalidateWorkCache(Maybe.just(values.id)),
    });

    const patchWorkMutation = useMutation({
        mutationFn: async (values: WorkHistoryItem): Promise<WorkHistoryItem> => {
            const { id, companyName, jobTitle, skills, ...restValues } = values;
            const data: WorkHistoryMutation = {
                ...cleanDeep(nullifyEmptyStringValues(restValues), { nullValues: false }),
                id,
                companyName,
                jobTitle,
                skillIds: skills?.map((skill) => skill.id) ?? [],
            };

            const result = await validatingCobrainerApiFetcher.patchJson(workHistoryItemSchema, {
                pathname: `/users/${userId}/work-history/${values.id}`,
                payload: data,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        onSuccess: (_, values) => invalidateWorkCache(Maybe.just(values.id)),
    });

    const deleteWorkMutation: UseMutationResult<void, unknown, string> = useMutation({
        mutationFn: async (id: string) => {
            const result = await cobrainerApiFetcher.delete({
                pathname: `/users/${userId}/work-history/${id}`,
            });

            if (result.isOk) {
                return;
            }

            throw result.error;
        },
        onSuccess: () => invalidateWorkCache(Maybe.nothing()),
    });

    return {
        createWorkMutation,
        deleteWorkMutation,
        patchWorkMutation,
    };
};
